import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Chip,
  Box,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import UseQsButton from "./utils/UseQsButton";

const tableData = [
  {
    type: "Kokegryte",
    items: [
      {
        subtype: "25 mm utløp",
        m: 1,
        q: 1.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "50 mm utløp",
        m: 2,
        q: 2.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
    ],
  },
  {
    type: "Tippbar kokegryte",
    items: [
      {
        subtype: "70 mm utløp",
        m: 3,
        q: 1.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "100 mm utløp",
        m: 4,
        q: 3.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
    ],
  },
  {
    type: "Spylekasse/oppvaskbenk",
    items: [
      {
        subtype: "Utløp med vannlås ø 40 mm",
        m: 5,
        q: 0.8,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "Utløp med vannlås ø 50 mm",
        m: 6,
        q: 1.5,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "Utløp uten vannlås ø 40 mm",
        m: 7,
        q: 2.5,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "Utløp uten vannlås ø 50 mm",
        m: 8,
        q: 4.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
    ],
  },
  {
    type: "Stekebord",
    items: [
      {
        subtype: "Tippbart",
        m: 10,
        q: 1.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "Fast",
        m: 11,
        q: 0.1,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
    ],
  },
  {
    type: "Høytrykksvask/dampvask",
    items: [
      { subtype: "", m: 12, q: 2.0, znFactors: [0.45, 0.31, 0.25, 0.21, 0.2] },
    ],
  },
  {
    type: "Skrape",
    items: [
      { subtype: "", m: 13, q: 1.5, znFactors: [0.45, 0.31, 0.25, 0.21, 0.2] },
    ],
  },
  {
    type: "Grønnsaksskyller",
    items: [
      { subtype: "", m: 14, q: 2.0, znFactors: [0.45, 0.31, 0.25, 0.21, 0.2] },
    ],
  },
  {
    type: "Tappeventil for rengjøring",
    items: [
      {
        subtype: "DN 15 mm",
        m: 15,
        q: 0.5,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "DN 20 mm",
        m: 16,
        q: 1.0,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
      {
        subtype: "DN 25 mm",
        m: 17,
        q: 1.7,
        znFactors: [0.45, 0.31, 0.25, 0.21, 0.2],
      },
    ],
  },
  {
    type: "Oppvaskmaskin",
    items: [
      { subtype: "", m: 9, q: 2.0, znFactors: [0.6, 0.45, 0.4, 0.34, 0.3] },
    ],
  },
];

function InputField({ label, value = 0, onChange }) {
  return (
    <TextField
      label={label}
      variant="outlined"
      value={value}
      onChange={onChange}
      fullWidth
      type="number"
    />
  );
}

function KitchenUtensilCalculator({handleSetQs}) {
  const [data, setData] = useState(tableData);

  const calculateTotalQs = () => {
    let totalQs = 0;
    data.forEach((group) => {
      group.items.forEach((item) => {
        if (item.used) {
          totalQs += calculateQs(item);
        }
      });
    });
    return totalQs
  };

  const updateQuantity = (sectionIndex, itemIndex, value) => {
    const newData = [...data];
    newData[sectionIndex].items[itemIndex].quantity = value;
    if (value > 0) {
      newData[sectionIndex].items[itemIndex].used = true;
    } else newData[sectionIndex].items[itemIndex].used = false;

    setData(newData);
  };

  const handleDeleteChip = (index, itemIndex) => {
    updateQuantity(index, itemIndex, 0);
  };

  const calculateZn = (item) => {
    if (!item.quantity) {
      return 0;
    }
    const n = item.quantity;

    if (n < 1) return 0;

    if (n >= 5) {
      return item.znFactors[4];
    } else {
      return item.znFactors[n - 1];
    }
  };

  const calculateQs = (item) => {
    if (!item.quantity) {
      return 0;
    }
    return item.q * item.quantity * calculateZn(item);
  };

  return (
    <div>
      <div>
        <UseQsButton Qs={calculateTotalQs()} handleSetQs={handleSetQs}/>
      </div>
      {data.map((group, index) => {
        const usedSubtypes = group.items.filter((item) => item.used);

        const totalSubtypeQs = usedSubtypes.reduce(
          (sum, item) => sum + calculateQs(item, item.quantity),
          0
        );
        return (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                  backgroudColor: "red",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="h6">{group.type}</Typography>
                  {group.items.map((item, itemIndex) => {
                    if (item.used) {
                      return (
                        <Chip
                          key={itemIndex}
                          label={
                            <Typography
                              sx={{ fontWeight: "bold", fontSize: "12px" }}
                            >
                              {item.subtype}
                            </Typography>
                          }
                          size="small"
                          sx={{
                            height: "22px",
                            "& .MuiChip-deleteIcon:hover": {
                              color: "red",
                            },

                            hidden: !data[index].items[itemIndex].used,
                            disabled: !data[index].items[itemIndex].used,
                          }}
                          onDelete={() => {
                            handleDeleteChip(index, itemIndex);
                          }}
                          deleteIcon={<CloseIcon />}
                        />
                      );
                    } else {
                      return null;
                    }
                  })}
                </Box>
                <Typography
                  sx={{ color: "gray", ml: "auto" }} // Set color to gray
                >
                  {`ΣQs: ${totalSubtypeQs.toFixed(2)}`}
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <Paper style={{ width: "100%" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>type</TableCell>
                      {/* <TableCell>m</TableCell> */}
                      {/* <TableCell>q</TableCell> */}
                      <TableCell>antall</TableCell>
                      {/* <TableCell>zn</TableCell> */}
                      <TableCell>Qs (L/s)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.items.map((item, itemIndex) => (
                      <TableRow key={itemIndex}>
                        <TableCell>{item.subtype}</TableCell>
                        {/* <TableCell>{item.m}</TableCell> */}
                        {/* <TableCell>{item.q}</TableCell> */}
                        <TableCell>
                          <InputField
                            value={item.quantity}
                            onChange={(e) => {
                              updateQuantity(index, itemIndex, e.target.value);
                            }}
                          />
                        </TableCell>
                        {/* <TableCell>{`${calculateZn(item).toFixed(
                          2
                        )}`}</TableCell> */}
                        <TableCell>{`${calculateQs(item).toFixed(
                          2
                        )}`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default KitchenUtensilCalculator;
